import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useHeaderVisibility } from '@/hooks/useHeaderVisibility';
import { cn } from '@/lib/utils';

/** Header height when shown; the Desk ribbon publishes its own height as --desk-ribbon-h. */
export const HEADER_H = 73;

/** CSS `top` for anything that docks below the header + Desk ribbon stack. */
export function stickyTop(headerVisible: boolean, extraPx = 0) {
  return `calc(${headerVisible ? HEADER_H : 0}px + var(--desk-ribbon-h, 0px) + ${extraPx}px)`;
}

/** Numeric px twin of `stickyTop` for hit-testing; reads the live ribbon height. */
function stickyTopPx(headerVisible: boolean): number {
  const ribbon = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--desk-ribbon-h'),
  ) || 0;
  return (headerVisible ? HEADER_H : 0) + ribbon;
}

/**
 * True once a sticky element is actually docked — its natural-flow position has
 * scrolled past the dock line. Drives the "floating band" look (background,
 * border, shadow) so a sticky toolbar shows no stray outline while the page
 * sits at the top.
 */
export function useStickyStuck(sentinelRef: RefObject<HTMLElement | null>, enabled: boolean) {
  const headerVisible = useHeaderVisibility();
  const [stuck, setStuck] = useState(false);

  const measure = useCallback(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !enabled) {
      setStuck(false);
      return;
    }
    setStuck(sentinel.getBoundingClientRect().bottom < stickyTopPx(headerVisible) + 1);
  }, [sentinelRef, enabled, headerVisible]);

  useEffect(() => {
    if (!enabled) {
      setStuck(false);
      return;
    }
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [enabled, measure]);

  return stuck;
}

/**
 * The one sticky search/filter toolbar used on every listing list (My Inventory,
 * menu inventory, supplier menus). Docks below the header and Desk ribbon.
 *
 * The floating band (background, border, rounded corners) paints only while the
 * toolbar is actually docked — at the top of the page it is invisible, so no
 * stray clipped-looking line shows under the filters.
 */
export function StickyToolbar({
  children,
  className,
  sticky = true,
}: {
  children: ReactNode;
  className?: string;
  sticky?: boolean;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const stuck = useStickyStuck(sentinelRef, sticky);
  const headerVisible = useHeaderVisibility();
  return (
    <>
      {sticky && <div ref={sentinelRef} aria-hidden className="h-0" />}
      <div
        data-sticky-toolbar
        data-stuck={stuck || undefined}
        style={sticky ? { top: stickyTop(headerVisible) } : undefined}
        className={cn(
          'relative space-y-3',
          sticky && 'sticky z-20 py-3 transition-[background-color,box-shadow] duration-200',
          sticky && stuck && [
            'rounded-b-lg bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80',
            'border-b border-border/60 shadow-[0_8px_24px_-16px_hsl(var(--foreground)/0.25)]',
          ],
          className,
        )}
      >
        {children}
      </div>
    </>
  );
}
